import { LayersControl, MapContainer, ZoomControl } from 'react-leaflet';
import { PropsWithChildren, useCallback, useRef } from 'react';
import { LatLngBounds, LatLngExpression, Map } from 'leaflet';
import TileLayers from '../tile-layers/TileLayers';
import './map.css';
import Skeleton from 'react-loading-skeleton';
import useDelay from '@cas/hooks/useDelay';
import MapManager from '../map-manager/MapManager';
import { MIN_ZOOM } from '../config';
import { MapProviderModel } from '@cas/types/settings';
import { useTranslation } from 'react-i18next';

const MAP_ID = 'map-container';

export interface MapViewProps {
  initialCentre?: LatLngExpression;
  initialZoom?: number;
  bounds?: LatLngBounds;
  showLayersControl?: boolean;
  padding?: boolean;
  animate?: boolean;
  renderKey?: string;
  afterInitCallback?: (map: Map) => void;
  useMapProvider: () => MapProviderModel;
}

export function MapView({
  initialCentre,
  initialZoom,
  bounds,
  showLayersControl,
  padding,
  animate,
  renderKey,
  afterInitCallback,
  useMapProvider,
  children,
}: PropsWithChildren<MapViewProps>) {
  const { t, i18n } = useTranslation();
  const mapRef = useRef<L.Map>(null);

  // Use the provided hook to get the data shape from an API.
  const { maxZoom, maxNativeZoom } = useMapProvider();

  const onMapReady = useCallback(() => {
    const resizeObserver = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
    });
    const c2 = document.getElementById(MAP_ID);
    if (c2 !== null) {
      resizeObserver.observe(c2);
    }
  }, []);

  // Delaying by a single millisecond is enough to resolve the map render issue on repeated mounts.
  // Without this delay, the map is calculated to be 0x0, breaking all of the zoom and bounds calculations.
  const isDelayFinished = useDelay(1);

  return isDelayFinished ? (
    <MapContainer
      id={MAP_ID}
      attributionControl={false}
      center={initialCentre ?? [0, 0]}
      // If no initialZoom is provided, attempt to set the zoom to 12 without exceeding the map config.
      zoom={Math.min(initialZoom || 12, maxNativeZoom)}
      bounds={bounds}
      scrollWheelZoom={true}
      ref={mapRef}
      whenReady={onMapReady}
      minZoom={MIN_ZOOM}
      maxZoom={maxZoom}
      zoomControl={false} // Disable the default zoom control
    >
      <TileLayers useMapProvider={useMapProvider} />

      <MapManager
        mapRef={mapRef}
        bounds={bounds}
        padding={padding}
        animate={animate}
        center={initialCentre}
        zoom={initialZoom}
        renderKey={renderKey}
        afterInitCallback={afterInitCallback}
        useMapProvider={useMapProvider}
      />

      {/* Manually include the zoom control to provide translations */}
      <ZoomControl
        key={`zoom-control-${i18n.language}`}
        position="topleft"
        zoomInTitle={t('ZoomIn')}
        zoomOutTitle={t('ZoomOut')}
      />

      {showLayersControl ? (
        <LayersControl key={`layers-control-${i18n.language}`} position="topright">
          {children}
        </LayersControl>
      ) : (
        children
      )}
    </MapContainer>
  ) : (
    <Skeleton width="100%" height="100%" />
  );
}

export default MapView;
